import React, { useEffect, useMemo, useState } from 'react';
import { Box, Tabs, Tab, Grid, Alert, FormControlLabel, Switch, Typography, TextField, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader/PageHeader';
import CustomTabPanel from '../../components/Tab/CustomTabPanel';
import { SitesProvider, useSitesConfig } from './SitesContext';
import SectionCard from './settings/SectionCard';
import SitesService from '../../services/SitesService';

// Importar componentes de tabs
import CompanyTab from './settings/CompanyTab';
import NavigationTab from './settings/NavigationTab';
import HeroTab from './settings/HeroTab';
import ColorsTab from './settings/ColorsTab';
import FontsTab from './settings/FontsTab';
import LocationsTab from './settings/LocationsTab';
import ContactTab from './settings/ContactTab';
import FooterTab from './settings/FooterTab';
import TextsTab from './settings/TextsTab';
import NewsletterTab from './settings/NewsletterTab';
import AboutTab from './settings/AboutTab';
import MenuTab from './settings/MenuTab';
import CareersTab from './settings/CareersTab';
import EcommerceTab from './settings/EcommerceTab';
import EnviosTab from './settings/EnviosTab';
import ImagesTab from './settings/ImagesTab';
import TopbarTab from './settings/TopbarTab';
import ReviewsTab from './settings/ReviewsTab';
import NotificationEmailTab from './settings/NotificationEmailTab';

const SECTION_COMPONENTS = {
  company: CompanyTab,
  nav: NavigationTab,
  hero: HeroTab,
  colors: ColorsTab,
  fonts: FontsTab,
  locations: LocationsTab,
  contact: ContactTab,
  footer: FooterTab,
  texts: TextsTab,
  newsletter: NewsletterTab,
  about: AboutTab,
  menu: MenuTab,
  reviews: ReviewsTab,
  careers: CareersTab,
  ecommerce: EcommerceTab,
  envios: EnviosTab,
  images: ImagesTab,
  topbar: TopbarTab,
  notificationEmail: NotificationEmailTab
};

// Prefijos de campos por sección para filtrar qué mostrar en General (según schema)
const SECTION_FIELD_PREFIXES = {
  company: 'siteConfig.company.',
  nav: 'siteConfig.nav.',
  topbar: 'siteConfig.company.', // logo/favicon
  colors: 'theme.colors.',
  fonts: 'theme.fonts.',
  images: 'siteConfig.company.logo' // cualquier campo de imagen para mostrar el tab
};

const SitesContent = () => {
  const { t } = useTranslation();
  const { config, handleChange, editableFields, isFieldConfigurable } = useSitesConfig();

  const getTabLabel = (page) => {
    const label = config?.siteConfig?.pages?.[page.id]?.tabLabel;
    if (label && String(label).trim()) return String(label).trim();
    return t(page.labelKey) || page.labelKey;
  };
  const [tabValue, setTabValue] = useState(0);
  const [selectedSection, setSelectedSection] = useState(null);

  const generalSections = editableFields?.generalSections ?? [];
  const templatePages = editableFields?.pages ?? [];
  const configurableFields = editableFields?.configurableFields ?? [];
  const ecommerceMode = config?.siteConfig?.ecommerceMode;
  const supportsEcommerceMode = configurableFields.includes('siteConfig.ecommerceMode');
  const hasMenuPage = templatePages.some((page) => page.id === 'menu');
  const visibleTemplatePages = useMemo(
    () => templatePages.filter((page) => {
      if (!supportsEcommerceMode && !hasMenuPage) return true;
      const effectiveEcommerceMode = ecommerceMode ?? 'one_page';
      if (page.id === 'tienda') return effectiveEcommerceMode === 'store';
      if (page.id === 'menu') return effectiveEcommerceMode !== 'store';
      return true;
    }),
    [templatePages, ecommerceMode, supportsEcommerceMode, hasMenuPage]
  );
  const hasGeneralSections = Array.isArray(generalSections) && generalSections.length > 0;
  const hasPageTabs = Array.isArray(visibleTemplatePages) && visibleTemplatePages.length > 0;

  useEffect(() => {
    const lastTabIndex = 1 + visibleTemplatePages.length;
    if (tabValue > lastTabIndex) {
      setTabValue(lastTabIndex);
    }
  }, [tabValue, visibleTemplatePages.length]);

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
    setSelectedSection(null);
  };

  const handleSelectSection = (section) => {
    setSelectedSection(section);
  };

  const handleBackToCards = () => {
    setSelectedSection(null);
  };

  const setPageEnabled = async (pageId, enabled) => {
    handleChange(`siteConfig.pages.${pageId}.enabled`, enabled);
    try {
      await SitesService.updateField(`siteConfig.pages.${pageId}.enabled`, JSON.stringify(enabled));
    } catch (err) {
      console.error('[Sites] Error saving page visibility:', err);
    }
  };

  const isPageEnabled = (pageId) => {
    const pages = config?.siteConfig?.pages ?? {};
    if (pages[pageId] && typeof pages[pageId].enabled === 'boolean') {
      return pages[pageId].enabled;
    }
    return true;
  };

  if (selectedSection) {
    const SectionComponent = SECTION_COMPONENTS[selectedSection];
    if (SectionComponent) {
      return (
        <Box p={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              component="button"
              onClick={handleBackToCards}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                mr: 2,
                color: 'text.primary',
                '&:hover': { color: 'primary.main' }
              }}
            >
              <Box component="span" sx={{ fontSize: '1.5rem', mr: 1 }}>←</Box>
              <Box component="span">{t('back') || 'Volver'}</Box>
            </Box>
          </Box>
          <PageHeader
            title={t(selectedSection) || selectedSection}
            subtitle={t(`${selectedSection}_subtitle`) || ''}
          />
          <SectionComponent />
        </Box>
      );
    }
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
        <Tabs
          value={tabValue}
          onChange={handleChangeTab}
          aria-label="sites tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label={t('general') || 'General'} />
          {hasPageTabs && visibleTemplatePages.map((p) => (
            <Tab key={p.id} label={getTabLabel(p)} />
          ))}
          <Tab label={t('footer') || 'Footer'} />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
        <PageHeader title={t('general') || 'General'} subtitle={t('general_subtitle') || 'Configuración general del sitio (empresa, colores, fuentes, imágenes)'} />
        <GeneralTabContent onSelectSection={handleSelectSection} />
      </CustomTabPanel>

      {hasPageTabs && visibleTemplatePages.map((page, idx) => {
        const PageSectionComponent = SECTION_COMPONENTS[page.sectionKey];
        const isInicio = page.id === 'inicio';
        const tabLabelPath = `siteConfig.pages.${page.id}.tabLabel`;
        const canEditTabLabel = isFieldConfigurable(tabLabelPath);
        const currentTabLabel = config?.siteConfig?.pages?.[page.id]?.tabLabel ?? '';
        return (
          <CustomTabPanel key={page.id} value={tabValue} index={1 + idx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <PageHeader
                title={getTabLabel(page)}
                subtitle={t(`${page.labelKey}_subtitle`) || `Configuración y visibilidad de la página`}
              />
              <Box sx={{ flexShrink: 0, pt: 0.5 }}>
                {isInicio ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('page_inicio_always_visible') || 'Página principal del sitio, siempre visible.'}
                  </Typography>
                ) : (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isPageEnabled(page.id)}
                        onChange={(e) => setPageEnabled(page.id, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('page_visible_on_site') || 'Visible en la web'}
                  />
                )}
              </Box>
            </Box>
            {canEditTabLabel && (
              <Paper sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('section_title') || 'Título'}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label={t('tab_name') || 'Nombre del tab'}
                  value={currentTabLabel}
                  onChange={(e) => handleChange(tabLabelPath, e.target.value)}
                  onBlur={async () => {
                    const value = config?.siteConfig?.pages?.[page.id]?.tabLabel ?? '';
                    try {
                      await SitesService.updateField(tabLabelPath, JSON.stringify(value));
                    } catch (err) {
                      console.error('[Sites] Error saving tab label:', err);
                    }
                  }}
                  placeholder={t(page.labelKey) || page.labelKey}
                />
              </Paper>
            )}
            {PageSectionComponent && <PageSectionComponent />}
          </CustomTabPanel>
        );
      })}

      <CustomTabPanel value={tabValue} index={1 + (visibleTemplatePages?.length ?? 0)}>
        <PageHeader
          title={t('footer') || 'Footer'}
          subtitle={t('footer_subtitle') || 'Configuración del pie de página (copyright, redes sociales)'}
        />
        <FooterTab />
      </CustomTabPanel>
    </Box>
  );
};

// Contenido del tab General: cards dinámicas desde schema (generalSections)
const GeneralTabContent = ({ onSelectSection }) => {
  const { t } = useTranslation();
  const { editableFields } = useSitesConfig();

  const configurableFields = editableFields?.configurableFields ?? [];
  const fixedFields = editableFields?.fixedFields ?? [];
  const generalSections = editableFields?.generalSections ?? [];

  const hasEditableFieldsInSection = (sectionKey) => {
    if (!Array.isArray(configurableFields)) return false;
    const prefix = SECTION_FIELD_PREFIXES[sectionKey];
    if (!prefix) return true;
    if (sectionKey === 'images') return configurableFields.some(f => f.includes('logo') || f.includes('Image') || f.includes('image'));
    // Nav puede estar declarado como campo fijo (de solo lectura) en vez de
    // configurable -- igual mostramos la tarjeta para que se vea ese estado.
    if (sectionKey === 'nav') {
      return configurableFields.some(field => field.startsWith(prefix)) ||
        (Array.isArray(fixedFields) && fixedFields.some(field => field.startsWith(prefix)));
    }
    return configurableFields.some(field => field.startsWith(prefix));
  };

  const defaultGeneralSections = [
    { key: 'company', labelKey: 'Company' },
    { key: 'nav', labelKey: 'navigation' },
    { key: 'topbar', labelKey: 'topbar' },
    { key: 'colors', labelKey: 'colors' },
    { key: 'fonts', labelKey: 'fonts' },
    { key: 'images', labelKey: 'images' },
    { key: 'ecommerce', labelKey: 'ecommerce' },
    { key: 'envios', labelKey: 'shipments' }
  ];
  const baseSections = Array.isArray(generalSections) && generalSections.length > 0 ? generalSections : defaultGeneralSections;
  // Asegurar que secciones operativas siempre aparezcan en General aunque el backend no las incluya.
  const sectionsSource = [...baseSections];
  if (!sectionsSource.some((s) => s.key === 'nav')) {
    sectionsSource.splice(1, 0, { key: 'nav', labelKey: 'navigation' });
  }
  if (!sectionsSource.some((s) => s.key === 'topbar')) {
    sectionsSource.splice(1, 0, { key: 'topbar', labelKey: 'topbar' });
  }
  if (!sectionsSource.some((s) => s.key === 'ecommerce')) {
    sectionsSource.push({ key: 'ecommerce', labelKey: 'ecommerce' });
  }
  if (!sectionsSource.some((s) => s.key === 'envios')) {
    sectionsSource.push({ key: 'envios', labelKey: 'shipments' });
  }
  if (!sectionsSource.some((s) => s.key === 'notificationEmail')) {
    sectionsSource.push({ key: 'notificationEmail', labelKey: 'site_email_notifications' });
  }
  const sectionsToShow = sectionsSource
    .filter(gs => SECTION_COMPONENTS[gs.key])
    .map(gs => ({
      key: gs.key,
      title: t(gs.labelKey) || gs.labelKey,
      subtitle: t(`${gs.labelKey}_subtitle`) || '',
      hasEditableFields: hasEditableFieldsInSection(gs.key)
    }))
    .filter(s => s.hasEditableFields);

  if (sectionsToShow.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay secciones generales configuradas para el template actual'}
        </Alert>
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      {sectionsToShow.map((section) => (
        <Grid item xs={12} sm={6} md={4} key={section.key}>
          <SectionCard
            title={section.title}
            subtitle={section.subtitle}
            onClick={() => onSelectSection(section.key)}
            hasEditableFields={section.hasEditableFields}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const Sites = () => {
  return (
    <SitesProvider>
      <SitesContent />
    </SitesProvider>
  );
};

export default Sites;
